export interface ShouldRedecideScheduleTabArgs {
  /** Whether the opening tab has already been decided once. */
  resolved: boolean
  /** Whether both games and events have settled (no fetch in flight). */
  inputsSettled: boolean
  /**
   * How many rows the CURRENTLY selected tab is showing. `undefined` means
   * "not known yet" and is deliberately not treated as empty.
   */
  currentTabItemCount: number | null | undefined
  /** Whether the user selected the current tab themselves. */
  tabPickedByUser?: boolean
  /**
   * Whether the current tab has emptied out SINCE the user selected it — i.e.
   * it had rows when they landed on it and no longer does.
   */
  emptiedSinceSelection?: boolean
}

/**
 * Whether a settled schedule list should re-run the tab decision.
 *
 * Creating or deleting an event can empty the tab the user is sitting on — a
 * team whose only upcoming item was an event lands on an empty Upcoming after
 * deleting it, and would otherwise stay there with nothing to show.
 *
 * The trigger is deliberately "the current tab is empty" rather than "a
 * mutation happened". Re-deciding on every mutation would move a user off a
 * tab they chose while it still had content — adding a future event while
 * reading Past would yank them to Upcoming.
 *
 * ## Deliberate selection wins
 *
 * An empty tab is not on its own a reason to move: opening Upcoming and
 * finding nothing there is a legitimate thing to do, and bouncing the user
 * straight back out of it makes the tab bar feel broken. So once the user has
 * picked a tab themselves, this stands down — UNLESS that tab emptied out
 * after they landed on it, which means the change came from a mutation rather
 * than from their choice, and moving them somewhere useful is again right.
 *
 * Both remaining guards matter: before the first decision the initial gate is
 * in charge, and mid-fetch an empty list means "still loading", not "emptied
 * out".
 */
export const shouldRedecideScheduleTab = ({
  resolved,
  inputsSettled,
  currentTabItemCount,
  tabPickedByUser = false,
  emptiedSinceSelection = false,
}: ShouldRedecideScheduleTabArgs): boolean => {
  if (!resolved || !inputsSettled) {
    return false
  }
  if (currentTabItemCount === null || currentTabItemCount === undefined) {
    return false
  }
  if (currentTabItemCount !== 0) {
    return false
  }
  if (tabPickedByUser && !emptiedSinceSelection) {
    return false
  }
  return true
}
